import { useState } from 'react';
import { KeyHandoffSignature } from 'src/SharedCustomHooks/UseSuiteActions/useKeyHandoffSubmission';

export type KeyPickupSigner = 'internal' | 'homeOwner';

export interface KeyPickupFormValues {
  internalSignature: KeyHandoffSignature | null;
  homeOwnerSignature: KeyHandoffSignature | null;
  notes: string;
  warrantyStartDate: string;
}

export interface UseKeyPickupFormStateReturn {
  values: KeyPickupFormValues;
  canSubmit: boolean;
  setNotes: (notes: string) => void;
  setWarrantyStartDate: (date: string) => void;
  setSignature: (
    signer: KeyPickupSigner,
    signature: KeyHandoffSignature
  ) => void;
  clearSignature: (signer: KeyPickupSigner) => void;
  reset: () => void;
}

const INITIAL_VALUES: KeyPickupFormValues = {
  internalSignature: null,
  homeOwnerSignature: null,
  notes: '',
  warrantyStartDate: '',
};

const signatureKeyFor = (
  signer: KeyPickupSigner
): 'internalSignature' | 'homeOwnerSignature' => {
  if (signer === 'internal') {
    return 'internalSignature';
  }

  return 'homeOwnerSignature';
};

export const useKeyPickupFormState = (): UseKeyPickupFormStateReturn => {
  const [values, setValues] = useState<KeyPickupFormValues>(INITIAL_VALUES);

  const setNotes = (notes: string) => {
    setValues((prev) => ({ ...prev, notes }));
  };

  const setWarrantyStartDate = (warrantyStartDate: string) => {
    setValues((prev) => ({ ...prev, warrantyStartDate }));
  };

  const setSignature = (
    signer: KeyPickupSigner,
    signature: KeyHandoffSignature
  ) => {
    const key = signatureKeyFor(signer);

    setValues((prev: KeyPickupFormValues) => ({ ...prev, [key]: signature }));
  };

  const clearSignature = (signer: KeyPickupSigner) => {
    const key = signatureKeyFor(signer);

    setValues((prev: KeyPickupFormValues) => ({ ...prev, [key]: null }));
  };

  const reset = () => {
    setValues(INITIAL_VALUES);
  };

  const canSubmit: boolean = Boolean(
    values.internalSignature?.signature &&
    values.homeOwnerSignature?.signature &&
    values.warrantyStartDate
  );

  return {
    values,
    canSubmit,
    setNotes,
    setWarrantyStartDate,
    setSignature,
    clearSignature,
    reset,
  };
};
